import { useState } from 'react';
import { api } from '../api';
import { useApi } from '../lib/useApi';
import { AreaChart, Donut } from '../lib/charts';
import { Card, CardHead, Empty, ErrorState, Kpi, Loading, Section } from '../components/ui';
import { delta, fmtDate, gbBytes, hrs, n0, n1, pct, plural, rub, tbBytes } from '../lib/format';
import { STATUS, STATUS_COLOR } from '../lib/plans';
import WorkspaceDrawer from './WorkspaceDrawer';

const METRICS = [
  ['comments', 'комментарии', '#9a6aff', n0],
  ['videos', 'видео', '#427fff', n0],
  ['users', 'новые люди', '#6cb8ff', n0],
  ['durationSec', 'хронометраж', '#d761af', hrs],
];

export default function Overview({ days, nonce }) {
  const [metric, setMetric] = useState('comments');
  const [openId, setOpenId] = useState(null);
  const { data, loading, error, reload } = useApi(() => api.summary({ days }), [days, nonce]);
  const series = useApi(() => api.series({ days, metric }), [days, metric, nonce]);

  if (loading) return <Loading what="сводку" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const t = data.tiles;
  const a = data.averages;
  const tr = data.trials;
  const [, , color, fmt] = METRICS.find(([k]) => k === metric);

  return (
    <>
      <div className="grid cards-6">
        <Kpi label="рабочих пространств живых" value={n0(t.workspacesLive)} unit={`из ${n0(t.workspacesTotal)}`}
          delta={delta(t.workspacesAdded, t.workspacesAddedPrev)} note={`+${n0(t.workspacesAdded)} за период`} />
        <Kpi label="людей в них" value={n0(t.people)}
          delta={delta(t.peopleAdded, t.peopleAddedPrev)} note={`${n1(a.peoplePerWorkspace)} на РП`} />
        <Kpi label="платят сейчас" value={n0(t.paying)} unit="РП"
          note={t.workspacesLive ? `${pct(t.paying / t.workspacesLive)} живых РП` : ''} />
        <Kpi label="выручка в месяц" value={n0(t.monthlyRevenue)} unit="₽"
          note={t.paying ? `средний чек ${rub(t.monthlyRevenue / t.paying)}` : ''} />
        <Kpi label="занято места" value={tbBytes(t.usedBytes)}
          note={t.limitBytes ? `${pct(t.usedBytes / t.limitBytes)} от купленного` : ''} />
        <Kpi label={`комментариев за ${days} ${plural(days, 'день', 'дня', 'дней')}`} value={n0(t.comments)}
          delta={delta(t.comments, t.commentsPrev)} note={`${n0(t.comments / days)} в день`} />
      </div>

      <Section title="что происходит" hint="сегодняшний день — неполные сутки, считается на лету" />
      <Card className="pad">
        <div className="filters">
          {METRICS.map(([key, label]) => (
            <button key={key} className={`chip${metric === key ? ' on' : ''}`} onClick={() => setMetric(key)}>{label}</button>
          ))}
          <span className="count">{days > 90 ? 'по неделям' : 'по дням'}</span>
        </div>
        {series.loading ? <Loading what="график" />
          : series.error ? <ErrorState error={series.error} onRetry={series.reload} />
          : <AreaChart data={series.data.map((d) => ({ date: d.date, v: d.value }))} height={190} color={color} fmt={fmt} />}
      </Card>

      <div className="grid cards-3" style={{ marginTop: 12 }}>
        <Card className="pad">
          <CardHead title="подписки по состоянию" caption="все РП, включая мёртвые" />
          <Donut parts={data.subscriptionsByStatus.filter((s) => s.count).map((s) => ({
            l: STATUS[s.status]?.ru || s.status, v: s.count, c: STATUS_COLOR[s.status] || '#3d4759',
          }))} />
        </Card>

        <Card className="pad">
          <CardHead title="триалы" caption="конверсия считается по завершённым триалам" />
          <div className="stat-lines">
            <div className="sl">
              <span className="sl-v">{n0(tr.running)}</span>
              <span className="sl-l">идёт прямо сейчас<br /><span className="muted">{n0(tr.endingThisWeek)} закончится в ближайшую неделю</span></span>
            </div>
            <div className="sl">
              <span className="sl-v" style={{ color: tr.conversion > 0.35 ? 'var(--ok)' : 'var(--amber)' }}>
                {tr.conversion === null || tr.conversion === undefined ? '—' : pct(tr.conversion)}
              </span>
              <span className="sl-l">доходит до оплаты<br /><span className="muted">{n0(tr.converted)} из {n0(tr.finished)} завершённых</span></span>
            </div>
            <div className="sl">
              <span className="sl-v">{n0(tr.startedInPeriod)}</span>
              <span className="sl-l">начато за период<br /><span className="muted">было {n0(tr.startedPrevPeriod)} периодом раньше</span></span>
            </div>
          </div>
        </Card>

        <Card className="pad">
          <CardHead title="в среднем на пространство" caption={`по ${n0(t.workspacesLive)} живым РП`} />
          <div className="stat-lines">
            <div className="sl"><span className="sl-v">{n1(a.peoplePerWorkspace)}</span>
              <span className="sl-l">{plural(Math.round(a.peoplePerWorkspace), 'человек', 'человека', 'человек')} в команде</span></div>
            <div className="sl"><span className="sl-v">{gbBytes(a.bytesPerWorkspace)}</span>
              <span className="sl-l">занято, это {pct(a.fillRate)} от тарифа</span></div>
            <div className="sl"><span className="sl-v">{n1(a.projectsPerWorkspace)}</span>
              <span className="sl-l">{plural(Math.round(a.projectsPerWorkspace), 'проект', 'проекта', 'проектов')} в работе</span></div>
            <div className="sl"><span className="sl-v">{n1(a.commentsPerDayPerWorkspace)}</span>
              <span className="sl-l">комментариев в день на РП</span></div>
          </div>
        </Card>
      </div>

      <div className="grid cards-3" style={{ marginTop: 12 }}>
        <Card className={`pad${data.attention.length ? ' glow' : ''}`}>
          <CardHead title="требует внимания"
            caption={`${n0(data.attention.length)} ${plural(data.attention.length, 'пространство', 'пространства', 'пространств')}`} />
          <div className="list">
            {data.attention.length ? data.attention.map((x) => (
              <button className="row" key={x.id} onClick={() => setOpenId(x.id)}>
                <span className="dot" style={{ background: ['#6cb8ff', '#f5d23a', '#f58b3a', '#ff5268'][x.severity - 1] }} />
                <span className="rn">{x.title}<div className="t-sub">{x.reason}</div></span>
                <span className="rv">{x.plan}</span>
              </button>
            )) : <Empty>всё спокойно</Empty>}
          </div>
        </Card>

        <Card className="pad">
          <CardHead title="больше всех занимают места" caption="в скобках — процент от своего тарифа" />
          <div className="list">
            {data.topByStorage.map((o) => {
              const space = (o.usage || []).find((u) => u.code === 'AVAILABLE_SPACE_MB');
              const rate = space && space.max ? space.used / space.max : null;
              return (
                <button className="row" key={o.id} onClick={() => setOpenId(o.id)}>
                  <span className="rn">{o.title}
                    <div className="t-sub">
                      {o.plan?.name} · {n0(o.members?.active)} {plural(o.members?.active || 0, 'человек', 'человека', 'человек')}
                    </div>
                  </span>
                  <span className="rv">{gbBytes(o.sizeBytes)}{' '}
                    {rate === null ? null : (
                      <span style={{ color: rate > 0.9 ? 'var(--bad)' : '#5b6474' }}>({pct(rate)})</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="pad">
          <CardHead title="больше всех комментируют" caption="за всё время жизни РП" />
          <div className="list">
            {data.topByComments.map((o) => (
              <button className="row" key={o.id} onClick={() => setOpenId(o.id)}>
                <span className="rn">{o.title}<div className="t-sub">{n0(o.videos)} видео · {hrs(o.durationSec)}</div></span>
                <span className="rv">{n0(o.comments)}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="stamp">данные собраны {fmtDate(data.generatedAt)} в {new Date(data.generatedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</div>

      <WorkspaceDrawer id={openId} onClose={() => setOpenId(null)} onOpen={setOpenId} />
    </>
  );
}
